import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import WebPageLayout from '../../../components/layout/WebPageLayout';
import DSLoading from '../../../components/designSystem/DSLoading';
import DSDeleteConfirmModal from '../../../components/designSystem/DSDeleteConfirmModal';
import CreatePostModal from './CreatePostModal';
import SafeHtmlViewer from '../../../components/common/SafeHtmlViewer';
import ProjectImage from '../../../components/common/ProjectImage';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectProjects, selectProjectsStatus, selectGetProjectStatus, selectSelectedProject, selectPosts, selectSearchPostsStatus } from '../../../Module/Project/Presentation/projectSelectors';
import { getProject, searchProjects, searchProjectPosts, createProjectPost, updateProjectPost, deleteProjectPost } from '../../../Module/Project/Presentation/projectActions';
import { selectSession } from '../../../Module/Auth/Presentation/authSelectors';
import { extractImageSlots, restoreImageSlots } from '../../../Utils/postImageSlots';
import { sanitizeProjectDescHtml } from '../../../Utils/projectDesc';
import ProjectDetailHeader from '../../../components/common/ProjectDetailHeader';
import kakaoIcon from '../../../assets/Icon/project/kakao_icon.png';
import emailIcon from '../../../assets/Icon/project/email_icon.png';
import kakaoExampleImg from '../../../assets/img/share_modal/kakao_example.png';
import emailExampleImg from '../../../assets/img/share_modal/email_example.png';
import { MOBILE_BREAKPOINT, convertToVwMob } from '../../../Utils/responsive';
import { useTranslation } from 'react-i18next';
import { openMailClient } from '../../../Utils/mail';

interface PostManagementWebProps {
  hasPosts: boolean;
}

const PostManagementWeb: React.FC<PostManagementWebProps> = () => {
  const { t } = useTranslation('translation');
  const location = useLocation();
  const dispatch = useAppDispatch();
  const session = useAppSelector(selectSession);
  const projects = useAppSelector(selectProjects);
  const projectsStatus = useAppSelector(selectProjectsStatus);
  const selectedProject = useAppSelector(selectSelectedProject);
  const getStatus = useAppSelector(selectGetProjectStatus);
  const posts = useAppSelector(selectPosts);
  const searchPostsStatus = useAppSelector(selectSearchPostsStatus);

  const projectId = (location.state as { projectId?: string } | undefined)?.projectId;

  // 세션 정보에서 userId 가져오기
  const userId = useMemo(() => {
    return (session as any)?.user?.id || window.localStorage.getItem('missionfund.userId') || '';
  }, [session]);

  useEffect(() => {
    if (projectId) {
      // 이미 로딩 중이거나 현재 프로젝트가 요청하려는 프로젝트와 같으면 중단
      if (getStatus === 'loading' || selectedProject?.project_id === projectId) {
        // 프로젝트는 있는데 포스트가 아직 로딩 전이면 포스트만 요청
        if (selectedProject?.project_id === projectId && searchPostsStatus === 'idle' && posts.length === 0) {
          dispatch(
            searchProjectPosts({
              limit: 1000,
              params: [{ fld: 'projectId', op: 'eq', val: projectId }],
              sort: 'created_at:desc',
            }),
          );
        }
        return;
      }
      dispatch(getProject({ project_id: projectId }));
      dispatch(
        searchProjectPosts({
          limit: 1000,
          params: [{ fld: 'projectId', op: 'eq', val: projectId }],
          sort: 'created_at:desc',
        }),
      );
    } else if (userId && projects.length === 0 && projectsStatus !== 'loading') {
      dispatch(
        searchProjects({
          limit: 100,
          params: [{ fld: 'user_id', op: 'eq', val: userId }],
          sort: 'created_at:desc',
        }),
      );
    }
  }, [dispatch, projectId, userId, projects.length, projectsStatus, getStatus, selectedProject?.project_id, searchPostsStatus, posts.length]);

  const currentProject = useMemo(() => {
    if (projectId) {
      const found = projects.find((p) => p.project_id === projectId);
      if (found) return found;
      if (selectedProject?.project_id === projectId) return selectedProject;
    }
    return selectedProject || projects[0];
  }, [projectId, selectedProject, projects]);

  const isProjectDetailPending =
    Boolean(projectId) &&
    selectedProject?.project_id !== projectId &&
    (getStatus === 'idle' || getStatus === 'loading');
  const isProjectListPending =
    !projectId &&
    Boolean(userId) &&
    projects.length === 0 &&
    (projectsStatus === 'idle' || projectsStatus === 'loading');
  const activeProjectId = projectId || currentProject?.project_id;
  const isPostsPending =
    Boolean(activeProjectId) &&
    posts.length === 0 &&
    (searchPostsStatus === 'idle' || searchPostsStatus === 'loading');
  const isLoading = isProjectDetailPending || isProjectListPending || isPostsPending;

  // 첫번째 프로젝트 로드 후 포스트 요청
  useEffect(() => {
    if (!projectId && currentProject?.project_id && searchPostsStatus === 'idle' && posts.length === 0) {
      dispatch(
        searchProjectPosts({
          limit: 1000,
          params: [{ fld: 'projectId', op: 'eq', val: currentProject.project_id }],
          sort: 'created_at:desc',
        }),
      );
    }
  }, [projectId, currentProject?.project_id, searchPostsStatus, posts.length, dispatch]);

  const projectTitle = currentProject?.title || t('common.projectTitlePlaceholder');
  const realPosts = posts;
  const hasPosts = realPosts.length > 0;

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sharePreviewType, setSharePreviewType] = useState<'kakao' | 'email' | null>(null);
  const [expandedPostIds, setExpandedPostIds] = useState<Set<string>>(new Set());

  const selectedPost = useMemo(() => {
    if (!selectedPostId) return null;
    return realPosts.find((p) => String((p as any)?._id ?? p.projectpost_id) === selectedPostId) || null;
  }, [realPosts, selectedPostId]);

  const buildSharePostTitle = useCallback(() => {
    const sourcePost = selectedPost ?? realPosts[0];
    const htmlContent =
      typeof (sourcePost as any)?.contents === 'string'
        ? (sourcePost as any).contents
        : typeof sourcePost?.update_message === 'string'
          ? sourcePost.update_message
          : '';
    const textContent = htmlContent.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (textContent) {
      const maxLength = 40;
      return textContent.length > maxLength ? `${textContent.slice(0, maxLength)}...` : textContent;
    }
    return t('page.postManagement.shareModal.defaultTitle');
  }, [realPosts, selectedPost, t]);

  const postShareUrl = useMemo(() => {
    const baseUrl = `http://go.MissionFund.org/${currentProject?.project_id || ''}`;
    return selectedPostId ? `${baseUrl}?postId=${selectedPostId}` : baseUrl;
  }, [currentProject?.project_id, selectedPostId]);

  const toggleExpand = useCallback((postId: string) => {
    setExpandedPostIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  }, []);

  // CreatePostModal 상태 관리
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [mediaType, setMediaType] = useState<'image' | 'video' | 'none'>('image');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [postContent, setPostContent] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUpdatingPost, setIsUpdatingPost] = useState(false);

  const handleOpen = useCallback(() => {
    setIsEditMode(false);
    setSelectedPostId(null);
    setIsOpen(true);
    setCurrentStep(1);
    setMediaType('image');
    setSelectedFile(null);
    setPreviewUrl(null);
    setVideoUrl('');
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsEditMode(false);
    setSelectedPostId(null);
    setCurrentStep(1);
    setMediaType('image');
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setVideoUrl('');
    setPostContent('');
    setIsUpdatingPost(false);
  }, [previewUrl]);

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // 이전 URL 정리
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  }, [previewUrl]);

  const handleNextStep = useCallback(async () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === 3) {
      // 3단계에서 완료 버튼 클릭 시
      if (isEditMode) {
        if (!selectedPostId || !currentProject?.project_id) return;

        const existingPost = realPosts.find((p) => String((p as any)?._id ?? p.projectpost_id) === selectedPostId);
        if (!existingPost) return;

        try {
          setIsUpdatingPost(true);
          const partner = currentProject.partner || 'OMF';
          const videoUrlTrimmed = videoUrl.trim();

          // 서버 전송 직전 HTML 정리 후 본문 인라인 이미지 추출/슬롯 할당
          const sanitizedPostContent = sanitizeProjectDescHtml(postContent);
          const { update_message, ...imageSlots } = extractImageSlots(sanitizedPostContent);

          await dispatch(
            updateProjectPost({
              projectpost_id: selectedPostId,
              project_id: currentProject.project_id,
              partner,
              update_message,
              video_url: mediaType === 'video' ? videoUrlTrimmed : '',
              image_url: previewUrl && !selectedFile ? previewUrl : (existingPost.image_url || ''),
              // 새 이미지 파일이 선택된 경우 (대표 이미지)
              image: mediaType === 'image' && selectedFile ? await readFileAsBase64(selectedFile) : undefined,
              // 기존 이미지 URL 유지 (새 base64가 없는 경우)
              image2_url: existingPost.image2_url || '',
              image3_url: existingPost.image3_url || '',
              image4_url: existingPost.image4_url || '',
              image5_url: existingPost.image5_url || '',
              ...imageSlots, // image2, image3... (base64)가 있으면 위 URL보다 우선순위가 높음
              image_desc: existingPost.image_desc || '',
              image2_desc: existingPost.image2_desc || '',
              image3_desc: existingPost.image3_desc || '',
              image4_desc: existingPost.image4_desc || '',
              image5_desc: existingPost.image5_desc || '',
              authentication_by: 'WEB',
              to_sponsor: existingPost.to_sponsor ?? false,
              emoticon01: existingPost.emoticon01 ?? 0,
              emoticon02: existingPost.emoticon02 ?? 0,
              emoticon03: existingPost.emoticon03 ?? 0,
              emoticon04: existingPost.emoticon04 ?? 0,
              emoticon05: existingPost.emoticon05 ?? 0,
              cheermsg: (existingPost as any).cheermsg || [],
            }),
          ).unwrap();

          handleClose();
          setShowSuccessAlert(true);

          // 리스트 새로고침
          dispatch(
            searchProjectPosts({
              limit: 1000,
              params: [{ fld: 'projectId', op: 'eq', val: currentProject.project_id }],
              sort: 'created_at:desc',
            }),
          );

          setTimeout(() => setShowSuccessAlert(false), 3000);
        } catch (e: any) {
          alert(e || t('page.postManagement.alerts.updateFail'));
        } finally {
          setIsUpdatingPost(false);
        }
        return;
      }

      if (!currentProject?.project_id) {
        alert(t('page.postManagement.alerts.selectProject'));
        return;
      }

      try {
        setIsUpdatingPost(true);
        const partner = currentProject.partner || 'OMF';
        const videoUrlTrimmed = videoUrl.trim();
        const imageBase64 =
          mediaType === 'image' && selectedFile ? await readFileAsBase64(selectedFile) : undefined;

        // 서버 전송 직전 HTML 정리 후 본문 인라인 이미지 추출/슬롯 할당
        const sanitizedPostContent = sanitizeProjectDescHtml(postContent);
        const { update_message, ...imageSlots } = extractImageSlots(sanitizedPostContent);

        // 1. 게시물 생성
        await dispatch(createProjectPost({
          project_id: currentProject.project_id,
          partner,
          update_message,
          video_url: mediaType === 'video' ? videoUrlTrimmed : '',
          image2_desc: '',
          image3_desc: '',
          image4_desc: '',
          image5_desc: '',
          image: imageBase64,
          ...imageSlots, // image2, image3... (base64)
          authentication_by: 'WEB',
          to_sponsor: false,
        })).unwrap();

        handleClose();
        setShowSuccessAlert(true);

        // 리스트 새로고침
        dispatch(
          searchProjectPosts({
            limit: 1000,
            params: [{ fld: 'projectId', op: 'eq', val: currentProject.project_id }],
            sort: 'created_at:desc',
          }),
        );

        // 3초 후 알림 자동 숨김
        setTimeout(() => {
          setShowSuccessAlert(false);
        }, 3000);
      } catch (e: any) {
        alert(e || t('page.postManagement.alerts.createFail'));
      } finally {
        setIsUpdatingPost(false);
      }
    }
  }, [currentStep, handleClose, currentProject, postContent, mediaType, selectedFile, dispatch, isEditMode, previewUrl, realPosts, selectedPostId, t, videoUrl]);

  const handlePrevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleMediaTypeChange = useCallback((type: 'image' | 'video' | 'none') => {
    setMediaType(type);

    if (type !== 'video') {
      setVideoUrl('');
    }

    // 미디어 타입 변경 시 이미지 파일은 image 타입에서만 유지
    if (type !== 'image') {
      setSelectedFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  const handleContentChange = useCallback((content: string) => {
    setPostContent(content);
  }, []);

  const handleRemoveMainMedia = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setSelectedFile(null);
  }, [previewUrl]);

  if (isLoading) {
    return (
      <WebPageLayout title={t('common.layout.projectManagement')} sidebarActive="posts">
        <Container>
          <DSLoading />
        </Container>
      </WebPageLayout>
    );
  }

  const toggleMenu = (postId: string) => {
    setOpenMenuId(openMenuId === postId ? null : postId);
  };

  const handleShare = (postId: string) => {
    setSelectedPostId(postId);
    setOpenMenuId(null);
    setIsShareModalOpen(true);
  };

  const handleEdit = (postId: string) => {
    const post = realPosts.find(p => String((p as any)?._id ?? p.projectpost_id) === postId);
    if (post) {
      setIsEditMode(true);
      setSelectedPostId(postId);

      const rawContent = typeof (post as any)?.contents === 'string'
        ? (post as any).contents
        : typeof post.update_message === 'string'
          ? post.update_message
          : '';

      // 슬롯 플레이스홀더를 다시 에디터용 이미지 태그로 복원
      setPostContent(restoreImageSlots(rawContent, post));

      setPreviewUrl(post.image_url || null);
      setVideoUrl(post.video_url ? String(post.video_url) : '');
      setMediaType(post.video_url ? 'video' : (post.image_url ? 'image' : 'none'));
      setIsOpen(true);
      setCurrentStep(1);
    }
    setOpenMenuId(null);
  };

  const handleDelete = (postId: string) => {
    setSelectedPostId(postId);
    setOpenMenuId(null);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPostId || !currentProject?.project_id) return;

    try {
      setIsDeletingPost(true);
      await dispatch(
        deleteProjectPost({
          projectpost_id: selectedPostId,
          project_id: currentProject.project_id,
          partner: currentProject.partner || 'OMF',
        }),
      ).unwrap();

      setIsDeleteModalOpen(false);
      setSelectedPostId(null);
      // 알림 표시
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
    } catch (e: any) {
      alert(e || t('page.postManagement.alerts.deleteFail'));
    } finally {
      setIsDeletingPost(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setSelectedPostId(null);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  const runShareKakao = () => {
    const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
    const shareTitle = buildSharePostTitle();

    if (!(window as any).Kakao) {
      const script = document.createElement('script');
      script.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';
      script.integrity = 'sha384-6MFdIr0zOira1CHQkedUqJVqe0ryQtcLs7ufRWw+T8rCXNKryCOgM/c7x5+z2JK7';
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        if (!(window as any).Kakao.isInitialized()) {
          (window as any).Kakao.init(KAKAO_KEY);
        }
        shareToKakao(shareTitle, postShareUrl);
      };
      document.head.appendChild(script);
    } else {
      if (!(window as any).Kakao.isInitialized()) {
        (window as any).Kakao.init(KAKAO_KEY);
      }
      shareToKakao(shareTitle, postShareUrl);
    }
  };

  const shareToKakao = (title: string, url: string) => {
    (window as any).Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: title,
        description: `${t('common.shareModal.descLine1')} ${t('common.shareModal.descLine2')}`,
        imageUrl: 'https://missionfund.org/static/media/main_logo.png',
        link: {
          mobileWebUrl: url,
          webUrl: url,
        },
      },
      buttons: [
        {
          title: t('common.shareModal.buttonDetail'),
          link: {
            mobileWebUrl: url,
            webUrl: url,
          },
        },
      ],
    });
  };

  const runShareEmail = () => {
    const shareTitle = buildSharePostTitle();
    const subject = t('common.shareModal.emailSubject', { title: shareTitle });
    const body = t('page.postManagement.shareModal.emailBody', { title: shareTitle, url: postShareUrl });
    openMailClient({ subject, body });
  };

  const handleShareKakao = () => {
    handleCloseShareModal();
    setSharePreviewType('kakao');
  };

  const handleShareEmail = () => {
    handleCloseShareModal();
    setSharePreviewType('email');
  };

  const handleCloseSharePreview = () => {
    setSharePreviewType(null);
  };

  const handleBackSharePreview = () => {
    setSharePreviewType(null);
    setIsShareModalOpen(true);
  };

  const handleConfirmSharePreview = () => {
    if (sharePreviewType === 'kakao') {
      runShareKakao();
    } else if (sharePreviewType === 'email') {
      runShareEmail();
    }
    setSharePreviewType(null);
  };

  const handleCopyLink = async () => {
    const url = postShareUrl;
    try {
      await navigator.clipboard.writeText(url);
      alert(t('common.messages.linkCopied'));
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(t('common.messages.linkCopied'));
    }
  };

  return (
    <WebPageLayout title={t('common.layout.projectManagement')} sidebarActive="posts">
      <Container>
        <ProjectDetailHeader title={projectTitle} projectId={currentProject?.project_id} variant="web" />

        <TitleRow>
          <PageTitle>{t('page.postManagement.pageTitle')}</PageTitle>
          {hasPosts && (
            <AddButton onClick={handleOpen}>
              <PlusIcon src="/Image/posts/plusIcon.png" alt={t('common.actions.add')} />
              {t('page.postManagement.actions.create')}
            </AddButton>
          )}
        </TitleRow>

        {!hasPosts ? (
          // 게시물이 없는 경우
          <>
            <ContentCard>
              <CardContent>
                <CardText>
                  <HighlightText>{t('page.postManagement.empty.highlight')}</HighlightText>
                  <br />
                  {t('page.postManagement.empty.line1')}
                  <br />
                  {t('page.postManagement.empty.line2')}
                </CardText>
                <PrimaryButton onClick={handleOpen}>
                  <PlusIcon src="/Image/posts/plusIcon.png" alt={t('page.postManagement.actions.create')} />
                  {t('page.postManagement.actions.create')}
                </PrimaryButton>
              </CardContent>
            </ContentCard>

            {showSuccessAlert && (
              <SuccessAlert>
                <CheckIconImage src="/Image/posts/check1.png" alt={t('common.status.complete')} />
                {t('page.postManagement.alerts.updateSuccess')}
              </SuccessAlert>
            )}

            <InfoBanner>
              <InfoIcon src="/Image/posts/idea_01.png" alt={t('page.postManagement.ideaAlt')} />
              {t('page.postManagement.infoBanner')}
            </InfoBanner>
            <BottomSpacer aria-hidden="true" />
          </>
        ) : (
          // 게시물이 있는 경우
          <>
            {showSuccessAlert && (
              <SuccessAlert>
                <CheckIconImage src="/Image/posts/check1.png" alt={t('common.status.complete')} />
                {t('page.postManagement.alerts.updateSuccess')}
              </SuccessAlert>
            )}

            <YellowBanner>
              <BannerContent>
                <BannerIcon src="/Image/posts/idea_01.png" alt={t('page.postManagement.ideaAlt')} />
                {t('page.postManagement.banner.message')}
              </BannerContent>
              <ShareButton
                onClick={() => {
                  const latestPostId = String((realPosts[0] as any)?._id ?? realPosts[0]?.projectpost_id ?? '');
                  setSelectedPostId(latestPostId || null);
                  setIsShareModalOpen(true);
                }}
              >
                <ShareIcon src="/Image/posts/shareIcon.png" alt={t('common.empty.shareAlt')} />
                {t('page.postManagement.actions.shareRecent')}
              </ShareButton>
            </YellowBanner>

            <PostList>
              {realPosts.map((post, idx) => {
                const postId = String((post as any)?._id ?? post.projectpost_id ?? idx);
                const isExpanded = expandedPostIds.has(postId);
                const postHtml =
                  typeof (post as any)?.contents === 'string'
                    ? (post as any).contents
                    : typeof post.update_message === 'string'
                      ? post.update_message
                      : '';

                // 우선순위: image_url > image2~5_url > HTML 내 첫 번째 이미지
                let displayImage = post.image_url || post.image2_url || post.image3_url || post.image4_url || post.image5_url;

                if (!displayImage && postHtml) {
                  const imgMatch = postHtml.match(/<img[^>]+src="([^">]+)"/);
                  if (imgMatch) {
                    displayImage = imgMatch[1];
                  }
                }

                return (
                  <PostCard key={postId}>
                    <PostHeader>
                      <PostDate>{post.created_at ? String(post.created_at).slice(0, 16).replaceAll('-', '.').replace('T', ' ') : ''}</PostDate>
                      <MoreButtonWrapper>
                        <MoreButton onClick={() => toggleMenu(postId)}>
                          <MoreIcon src="/Image/posts/moreIcon.png" alt={t('common.actions.more')} />
                        </MoreButton>
                        {openMenuId === postId && (
                          <KebabMenu>
                            <MenuItem onClick={() => handleShare(postId)}>{t('common.actions.share')}</MenuItem>
                            <MenuItem onClick={() => handleEdit(postId)}>{t('common.actions.edit')}</MenuItem>
                            <MenuItem onClick={() => handleDelete(postId)}>{t('common.actions.delete')}</MenuItem>
                          </KebabMenu>
                        )}
                      </MoreButtonWrapper>
                    </PostHeader>
                    <PostHeaderDivider />
                    {displayImage && (
                      <PostImage
                        as={ProjectImage}
                        idOrUrl={displayImage}
                        alt=""
                      />
                    )}
                    <PostContent>
                      <PostText isExpanded={isExpanded}>
                        <SafeHtmlViewer html={postHtml} postData={post} />
                      </PostText>
                    </PostContent>
                    <ButtonWrapper>
                      <DetailButton onClick={() => toggleExpand(postId)}>
                        {isExpanded ? t('page.postManagement.toggle.collapse') : t('page.postManagement.toggle.expand')}
                        <DownIcon
                          src="/Image/posts/downIcon.png"
                          alt={isExpanded ? t('page.postManagement.toggle.collapseAlt') : t('page.postManagement.toggle.expandAlt')}
                          style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }}
                        />
                      </DetailButton>
                    </ButtonWrapper>
                  </PostCard>
                );
              })}
            </PostList>
          </>
        )}
      </Container>

      <DSDeleteConfirmModal
        open={isDeleteModalOpen}
        itemName={t('page.postManagement.itemName')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeletingPost}
      />

      <CreatePostModal
        isOpen={isOpen}
        isEditMode={isEditMode}
        currentStep={currentStep}
        mediaType={mediaType}
        previewUrl={previewUrl}
        videoUrl={videoUrl}
        postContent={postContent}
        isLoading={isUpdatingPost}
        onClose={handleClose}
        onFileSelect={handleFileSelect}
        onNextStep={handleNextStep}
        onPrevStep={handlePrevStep}
        onMediaTypeChange={handleMediaTypeChange}
        onContentChange={handleContentChange}
        onVideoUrlChange={setVideoUrl}
        onRemoveMainMedia={handleRemoveMainMedia}
      />

      {/* 공유 모달 */}
      {isShareModalOpen && (
        <ShareModalOverlay onClick={handleCloseShareModal}>
          <ShareModalContent onClick={(e) => e.stopPropagation()}>
            <ShareModalHeader>
              <ShareModalTitleRow>
                <ShareModalTitle>{t('page.postManagement.shareModal.title')}</ShareModalTitle>
                <CloseButton onClick={handleCloseShareModal} aria-label="close" />
              </ShareModalTitleRow>
              <ShareModalDescription>
                {t('common.shareModal.descLine1')} {t('common.shareModal.descLine2')}
              </ShareModalDescription>
            </ShareModalHeader>

            <ShareOptionsContainer>
              <ShareOption onClick={handleShareKakao}>
                <KakaoIconBox>
                  <KakaoIconImg src={kakaoIcon} alt={t('common.shareModal.kakaoAlt')} />
                </KakaoIconBox>
                <ShareOptionText>{t('common.shareModal.kakaoOption')}</ShareOptionText>
              </ShareOption>

              <ShareOption onClick={handleShareEmail}>
                <EmailIconBox>
                  <EmailIconLarge src={emailIcon} alt={t('common.shareModal.emailAlt')} />
                </EmailIconBox>
                <ShareOptionText>{t('common.shareModal.emailOption')}</ShareOptionText>
              </ShareOption>
            </ShareOptionsContainer>

            <LinkCopyWrapper>
              <LinkCopyContainer>
                <LinkInputSection>
                <LinkLabel>{t('page.postManagement.shareModal.linkLabel')}</LinkLabel>
                  <LinkInput
                    type="text"
                    value={postShareUrl}
                    readOnly
                  />
                </LinkInputSection>
                <Divider />
                <CopyButton onClick={handleCopyLink}>
                  <CopyIcon src="/Image/posts/copyIcon.png" alt={t('common.actions.copy')} />
                  {t('common.shareModal.copyLink')}
                </CopyButton>
              </LinkCopyContainer>
            </LinkCopyWrapper>
          </ShareModalContent>
        </ShareModalOverlay>
      )}
      {sharePreviewType && (
        <SharePreviewOverlay onClick={handleCloseSharePreview}>
          <SharePreviewContainer onClick={(e) => e.stopPropagation()}>
            <SharePreviewHeader>
              <SharePreviewTitleRow>
                <SharePreviewTitle>
                  {sharePreviewType === 'kakao' ? t('common.shareModal.kakaoOption') : t('common.shareModal.emailOption')}
                </SharePreviewTitle>
                <SharePreviewCloseButton onClick={handleCloseSharePreview} aria-label="close" />
              </SharePreviewTitleRow>
              <SharePreviewDesc>
                {sharePreviewType === 'kakao' ? '카톡으로 새 모금 프로젝트를 전합니다.' : '이메일로 새소식을 전합니다.'}
              </SharePreviewDesc>
            </SharePreviewHeader>
            <SharePreviewBody>
              <SharePreviewImage
                src={sharePreviewType === 'kakao' ? kakaoExampleImg : emailExampleImg}
                alt={sharePreviewType === 'kakao' ? t('common.shareModal.kakaoAlt') : t('common.shareModal.emailAlt')}
              />
            </SharePreviewBody>
            <SharePreviewFooter>
              <SharePreviewBackButton onClick={handleBackSharePreview}>
                {t('common.actions.back')}
              </SharePreviewBackButton>
              <SharePreviewSendButton onClick={handleConfirmSharePreview}>
                보내기
              </SharePreviewSendButton>
            </SharePreviewFooter>
          </SharePreviewContainer>
        </SharePreviewOverlay>
      )}
    </WebPageLayout>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding-right: 0;
`;

const BottomSpacer = styled.div`
  margin-bottom: 15vh;
`;

const PageTitle = styled.h1`
  ${({ theme }) => theme.typography.head.semiBold24}
  color: #000000;
  margin: 0;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const AddButton = styled.button`
  display: flex;
  padding: 14px 44px;
  align-items: center;
  gap: 8px;
  border-radius: 50px;
  background: var(--PrimaryBlue-70, #357B89);
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  color: ${({ theme }) => theme.colors.grayScale[0]};
  border: none;
${({ theme }) => theme.typography.button.bold24}
  cursor: pointer;

  &:hover {
    border-radius: 50px;
    background: #026478;
    box-shadow: 0 0 5.8px 0 #026478;
  }

  &:active {
    border-radius: 50px;
    background: #416777;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  }
`;

const ContentCard = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 20px;
  padding: 28px 48px;
  margin-bottom: 20px;
  width: 100%;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 29px;
`;

const CardText = styled.div`
  ${({ theme }) => theme.typography.contents.regular24};
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const HighlightText = styled.span`
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
  ${({ theme }) => theme.typography.contents.regular24}
`;

const PlusIcon = styled.img`
  width: 14px;
  height: 14px;
  object-fit: fill;
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--PrimaryBlue-70, #357B89);
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  color: ${({ theme }) => theme.colors.grayScale[0]};
  border: none;
  border-radius: 50px;
  padding: 14px 44px;
  ${({ theme }) => theme.typography.button.bold24}
  cursor: pointer;
  width: fit-content;

  &:hover {
    border-radius: 50px;
    background: #026478;
    box-shadow: 0 0 5.8px 0 #026478;
  }

  &:active {
    border-radius: 50px;
    background: #416777;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  }
`;

const SuccessAlert = styled.div`
  background: ${({ theme }) => theme.colors.primaryBlue[10]};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  padding: 10px 10px;
  border: 1px solid ${({ theme }) => theme.colors.primaryBlue[20]};
  ${({ theme }) => theme.typography.contents.regular20}
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 16px;
`;

const CheckIconImage = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
  flex-shrink: 0;
`;

const InfoBanner = styled.div`
  background: ${({ theme }) => theme.colors.yellow[5]};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  padding: 28px 48px;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.yellow[10]};
  ${({ theme }) => theme.typography.contents.regular20}
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
`;

const InfoIcon = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
`;

// 게시물이 있을 때 사용하는 컴포넌트들
const YellowBanner = styled.div`
  background: ${({ theme }) => theme.colors.yellow[5]};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${({ theme }) => theme.typography.contents.regular20}
  padding: 28px 48px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid ${({ theme }) => theme.colors.yellow[10]};
  gap: 16px;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BannerIcon = styled.img`
  width: 28px;
  height: 28px;
  object-fit: fill;
  flex-shrink: 0;
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: ${({ theme }) => theme.colors.yellow[20]}; 
  color: ${({ theme }) => theme.colors.grayScale[0]};
  border: none;
  border-radius: 50px;
  padding: 10px 40px;
  ${({ theme }) => theme.typography.button.semiBold20}
  cursor: pointer;
  width: 305px;
  height: 50px;
  margin-left: 40px;

  &:hover {
    background: ${({ theme }) => theme.colors.yellow[30]};
    box-shadow: 0px 0px 5.4px 0px;
  }

  &:active {
      background: ${({ theme }) => theme.colors.yellow[40]};
  }
`;

const ShareIcon = styled.img`
  width: 30px;
  height: 30px;
  object-fit: contain;
  aspect-ratio: 1/1;
`;

const PostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 15vh;
`;

const PostCard = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
  }
`;

const PostHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 28px;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const PostHeaderDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.grayScale[20]};
  width: 100%;
`;

const PostDate = styled.span`
   color: #676767;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
`;

const MoreButtonWrapper = styled.div`
  position: relative;
`;

const MoreButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const KebabMenu = styled.div`
  position: absolute;
  top: 40px;
  right: 0;
  background: #ffffff;
  border: 1px solid #EEEEEE;
  border-radius: 8px;
  box-shadow: 0px 4px 0px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  z-index: 100;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px 16px;
`;

const MenuItem = styled.button`
  width: 100%;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: none;
  ${({ theme }) => theme.typography.contents.regular20}
  color: #404040;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
  text-decoration: underline;
    }

`;

const MoreIcon = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const PostImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const PostContent = styled.div`
  padding: 28px 32px;
`;

const PostText = styled.div<{ isExpanded: boolean }>`
  ${({ theme }) => theme.typography.contents.regular24}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
  line-height: 1.7;

  ${({ isExpanded }) =>
    !isExpanded &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;

    figure[data-embed="story-image"] {
      display: none;
    }
  `}

  ${({ isExpanded }) =>
    isExpanded &&
    `
    figure[data-embed="story-image"] {
      display: block;
    }
  `}
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 11px 60px 11px;
`;

const DetailButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 250px;
  height: 60px;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[50]};
  border-radius: 30px;
  gap: 12px;
  ${({ theme }) => theme.typography.contents.regular20}
  color: #1A1A1A;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.grayScale[6]};
  }

  &:active {
    background: ${({ theme }) => theme.colors.grayScale[10]};
  }
`;

const DownIcon = styled.img`
  width: 12px;
  height: 12px;
  object-fit: contain;
`;

// 공유 모달 스타일
const ShareModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const ShareModalContent = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border-radius: 12px;
  width: 100%;
  height: 389px;
  max-width: 644px;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.2);
`;

const ShareModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px 30px 20px 30px;
`;

const ShareModalTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ShareModalTitle = styled.h2`
  ${({ theme }) => theme.typography.head.semiBold24}
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  outline: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M19.7011 1.74203C19.8925 1.55065 20 1.2911 20 1.02045C20 0.749813 19.8925 0.490257 19.7011 0.298884C19.5097 0.107512 19.2502 0 18.9795 0C18.7089 0 18.4493 0.107512 18.258 0.298884L10 8.55686L1.74203 0.298884C1.55065 0.107512 1.2911 1.97053e-08 1.02045 2.17217e-08C0.749813 2.37381e-08 0.490257 0.107512 0.298884 0.298884C0.107512 0.490257 2.37381e-08 0.749813 2.17217e-08 1.02045C1.97053e-08 1.2911 0.107512 1.55065 0.298884 1.74203L8.55686 10L0.298884 18.258C0.107512 18.4493 0 18.7089 0 18.9795C0 19.2502 0.107512 19.5097 0.298884 19.7011C0.490257 19.8925 0.749813 20 1.02045 20C1.2911 20 1.55065 19.8925 1.74203 19.7011L10 11.4431L18.258 19.7011C18.4493 19.8925 18.7089 20 18.9795 20C19.2502 20 19.5097 19.8925 19.7011 19.7011C19.8925 19.5097 20 19.2502 20 18.9795C20 18.7089 19.8925 18.4493 19.7011 18.258L11.4431 10L19.7011 1.74203Z' fill='%23929292'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;

  &:hover {
    opacity: 0.9;
  }
`;

const ShareModalDescription = styled.p`
  ${({ theme }) => theme.typography.contents.medium20}
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  margin: 0;
`;

const ShareOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 32px 20px 32px;
`;

const ShareOption = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  width: 100%;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 0;
`;


const KakaoIconBox = styled.div`
  width: 60px;
  height: 60px;
  background: #f9e000;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KakaoIconImg = styled.img`
  width: 34px;
  height: 34px;
`;

const EmailIconBox = styled.div`
  width: 60px;
  height: 60px;
  background: #EBFCFF;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmailIconLarge = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
`;

const ShareOptionText = styled.span`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  text-align: left;
  line-height: 150%;
  flex: 1;
`;

const LinkCopyWrapper = styled.div`
  display: flex;
  gap: 0;
  align-items: center;
  padding: 20px 30px;
  background: ${({ theme }) => theme.colors.grayScale[6] || '#f8f8f8'};
`;

const LinkCopyContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 70px;
  border-radius: 10px;
  border: 1px solid #929292;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  overflow: hidden;
`;

const LinkInputSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  padding: 10px 16px;
  height: 100%;
  justify-content: center;
`;

const LinkLabel = styled.label`
  ${({ theme }) => theme.typography.contents.regular16}
  color: ${({ theme }) => theme.colors.grayScale[50]};
`;

const LinkInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[70]};
  outline: none;
  padding: 0;
  flex: 1;
`;

const Divider = styled.div`
  width: 1px;
  height: 100%;
  background: #929292;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 20px 32px;
  background: #ffffff;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  border: none;
  ${({ theme }) => theme.typography.button.regular20}
  height: 100%;
  cursor: pointer;
`;

const CopyIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const SharePreviewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 24px;
`;

const SharePreviewContainer = styled.div`
  width: 644px;
  max-width: calc(100vw - 48px);
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border-radius: 12px;
  overflow: hidden;
`;

const SharePreviewHeader = styled.div`
  padding: 32px 30px 20px;
  background: var(--GreyScale-6, #F8F8F8);
`;

const SharePreviewTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SharePreviewTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
`;

const SharePreviewCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M19.7011 1.74203C19.8925 1.55065 20 1.2911 20 1.02045C20 0.749813 19.8925 0.490257 19.7011 0.298884C19.5097 0.107512 19.2502 0 18.9795 0C18.7089 0 18.4493 0.107512 18.258 0.298884L10 8.55686L1.74203 0.298884C1.55065 0.107512 1.2911 1.97053e-08 1.02045 2.17217e-08C0.749813 2.37381e-08 0.490257 0.107512 0.298884 0.298884C0.107512 0.490257 2.37381e-08 0.749813 2.17217e-08 1.02045C1.97053e-08 1.2911 0.107512 1.55065 0.298884 1.74203L8.55686 10L0.298884 18.258C0.107512 18.4493 0 18.7089 0 18.9795C0 19.2502 0.107512 19.5097 0.298884 19.7011C0.490257 19.8925 0.749813 20 1.02045 20C1.2911 20 1.55065 19.8925 1.74203 19.7011L10 11.4431L18.258 19.7011C18.4493 19.8925 18.7089 20 18.9795 20C19.2502 20 19.5097 19.8925 19.7011 19.7011C19.8925 19.5097 20 19.2502 20 18.9795C20 18.7089 19.8925 18.4493 19.7011 18.258L11.4431 10L19.7011 1.74203Z' fill='%23929292'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const SharePreviewDesc = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const SharePreviewBody = styled.div`
  padding: 20px 30px;
  display: flex;
  justify-content: center;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const SharePreviewImage = styled.img`
  width: 100%;
  max-width: 360px;
  object-fit: contain;
`;

const SharePreviewFooter = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[6] || '#f8f8f8'};
  padding: 20px 30px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const SharePreviewBackButton = styled.button`
  height: 70px;
  min-width: 146px;
  border-radius: 10px;
  border: 1px solid #c7c7c7;
  background: #fff;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  ${({ theme }) => theme.typography.button.regular20}
  cursor: pointer;
`;

const SharePreviewSendButton = styled.button`
  height: 70px;
  min-width: 146px;
  border-radius: 10px;
  border: none;
  background: #0a667f;
  color: #fff;
  ${({ theme }) => theme.typography.button.regular20}
  cursor: pointer;
`;

export default PostManagementWeb;

